import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import format from 'date-fns/format';
import { Box } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import CustomTable from '../../../components/Table/Table';
import Button from '../../../components/Button/CustomButton';
import GridItem from '../../../components/Grid/GridItem';
import CustomTypography from '../../../components/Typography/CustomTypography';
import SingleDatePickerSearch from '../../../components/DatePicker/SingleDatePickerSearch';
import GridContainer from '../../../components/Grid/GridContainer';
import CleanableInput from '../../../components/Input/CleanableInput';
import Loader from '../../../components/Loader/Loader';
import dateUtils, { convertDuration } from '../../../utils/dateUtils';
import DialogEditSeminar from '../CompanyManagement/DialogEditSeminar';
import Snackbar from '../../../components/Snackbar/Snackbar';
import AlertDialog from '../../../components/Dialog/AlertDialog';
import styles from '../../../assets/jss/pages/listAllSeminarsStyle';
import { SEMINAR_STATUS, APIErrors } from '../../../common/Constants';
import {
    LoadMoreIcon,
} from '../../../components/Icons/Icon';

const useStyles = makeStyles(styles);

const ActionRow = ({
    onEdit, onPause, stopped, ...rest
}) => (
    <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" width="100%">
        {
            !stopped && (
                <>
                    <Button disabled={stopped} onClick={onEdit} style={{ fontSize: '14px' }} color="success" {...rest}>編集</Button>
                    &nbsp;
                    <Button disabled={stopped} onClick={onPause} style={{ fontSize: '14px' }} color="danger" {...rest}>中止</Button>
                </>
            )
        }
    </Box>
);

const ListAllSeminar = ({
    fetchAllSeminars,
    isLoading,
    seminars,
    pagination,
    resetSeminarsData,
    eventInfo,
    getInformationRoom,
    room,
    getDetailSeminar,
    detailSeminar,
    updateSeminar,
    updateSuccess,
    updateErrors,
    resetUpdateSeminarData,
    resetDetailSeminar,
    detailSeminarErrors,
}) => {
    const classes = useStyles();
    const intl = useIntl();
    const [params, setParams] = useState({});
    const [isOpenDialogEdit, setOpenDialogEdit] = useState(false);
    const [roomInfo, setRoomInfo] = useState({});
    const [openUpdateSuccessAlert, setUpdateSuccessAlert] = useState(false);
    const [openUpdateErrorAlert, setUpdateErrorAlert] = useState({ isOpen: false, content: '' });
    const [isOpenConfirmStopSeminar, setOpenConfirmStopSeminar] = useState({ isOpen: false, isWaiting: false });
    const [openStopSeminarSuccessAlert, setOpenStopSeminarSuccessAlert] = useState(false);
    const seminarIdSelected = useRef(''); // current seminar id selected when click button stop
    const routeParams = useParams();

    useEffect(() => {
        fetchAllSeminars();
        return () => {
            resetSeminarsData();
        };
    }, []);

    useEffect(() => {
        if (room) {
            setRoomInfo({ ...room });
        } else {
            setRoomInfo({});
        }
    }, [room]);

    useEffect(() => {
        if (updateSuccess) {
            resetUpdateSeminarData();
            if (seminarIdSelected.current) {
                setOpenStopSeminarSuccessAlert(true);
                seminarIdSelected.current = '';
                setOpenConfirmStopSeminar({ isOpen: false, isWaiting: false });
            } else {
                setOpenDialogEdit(false);
                setUpdateSuccessAlert(true);
            }
            resetDetailSeminar();
            resetSeminarsData();
            fetchAllSeminars();
        } else if (!_.isEmpty(updateErrors)) {
            if (updateErrors.code === APIErrors.badRequest) {
                setUpdateErrorAlert({ isOpen: true, content: updateErrors?.message?.message || updateErrors?.non_field_errors?.message });
            }
            if (seminarIdSelected.current) {
                seminarIdSelected.current = '';
                setOpenConfirmStopSeminar({ isOpen: false, isWaiting: false });
                setUpdateErrorAlert({ isOpen: true, content: updateErrors?.is_paused && updateErrors?.is_paused?.message });
            } else {
                setUpdateErrorAlert({ isOpen: true, content: intl.formatMessage({ id: 'apiErrors.default' }) });
                setOpenDialogEdit(false);
            }
            resetUpdateSeminarData();
            resetSeminarsData();
            fetchAllSeminars();
        }
    }, [updateSuccess, updateErrors]);

    const createDataView = () => {
        const viewTotal = [];
        (Object.keys(seminars).forEach((key) => {
            createDateTitle(key);
            const viewTable = [];
            Object.keys(seminars[key]).forEach((roomData) => {
                const dataRow = [];
                const dataId = [];
                const registeredArray = [];
                const dataDateTime = [];
                if (seminars[key][roomData]) {
                    seminars[key][roomData].forEach((seminar) => {
                        const costPaid = seminar.cost > 0 ? `${intl.formatNumber(seminar.cost)} 円` : '無料';
                        dataRow.push([
                            `${seminar.stime}~${seminar.etime}`,
                            seminar.title,
                            seminar.content,
                            seminar.speaker,
                            costPaid,
                            convertDuration(
                                seminar.media_duration,
                                intl.formatMessage({ id: 'common.hour' }),
                                intl.formatMessage({ id: 'common.minute' }),
                                intl.formatMessage({ id: 'common.second' }),
                            ),
                            seminar.is_paused ? '停止中' : '通常',
                            <ActionRow
                                stopped={seminar.is_paused}
                                onEdit={(event) => handleEditSeminar(event, seminar.id)}
                                onPause={(event) => handleStopSeminar(event, seminar.id, seminar.is_in_order_waiting)}
                            />,
                        ]);
                        dataId.push(seminar.id);
                        registeredArray.push(seminar.registered);
                        dataDateTime.push({ date: seminar.date, stime: seminar.stime, etime: seminar.etime });
                    });
                }
                viewTable.push(createTable(roomData, dataRow, dataId, dataDateTime, registeredArray));
            });
            const viewDate = (
                <>
                    {createDateTitle(key)}
                    {viewTable}
                </>
            );
            viewTotal.push(viewDate);
        }));
        return viewTotal;
    };

    const createDateTitle = (dateTitle) => (
        <GridItem xs={12} style={{ margin: '20px 0 20px 0' }}>
            <CustomTypography bold variant="h5">
                開催日
                {'  :  '}
                {dateUtils.formatJa(new Date(dateTitle))}
            </CustomTypography>
        </GridItem>
    );

    const createTable = (title, dataRow) => (
        <GridItem xs={12}>
            <CustomTypography
                variant="subtitle1"
                bold
            >
                <li>
                    {`会議室 ${title}`}
                </li>
            </CustomTypography>
            <CustomTable
                tableHead={['時間', '講演名', '講演内容', '講演者', '料金', '再生時間', '状況', 'アクション']}
                tableHeadWidth={['10%', '10%', '17.5%', '17.5%', '10%', '10%', '10%', '15%']}
                tableData={dataRow}
                hover={false}
            />
        </GridItem>
    );

    const handleLoadMoreSeminar = () => {
        if (fetchAllSeminars) {
            fetchAllSeminars({ page: pagination.page + 1 });
        }
    };

    const handleSetSearchParams = useCallback((field, value) => {
        setParams((prevParams) => ({ ...prevParams, [field]: value }));
    }, []);

    const handleSearchSeminar = () => {
        if (fetchAllSeminars) {
            resetSeminarsData();
            fetchAllSeminars(params);
        }
    };

    const handleGetRoomInfo = (dateSeminar, seminarId) => {
        const date = format(new Date(dateSeminar), 'yyyy-MM-dd');
        getInformationRoom({
            shortName: routeParams.shortName,
            date,
            seminar_id: seminarId,
        });
    };

    const handleEditSeminar = (event, seminar_id) => {
        event.stopPropagation();
        setOpenDialogEdit(true);
        getDetailSeminar({
            seminar_id,
        });
    };

    const handleStopSeminar = (event, seminar_id, waiting) => {
        event.stopPropagation();
        seminarIdSelected.current = seminar_id;
        setOpenConfirmStopSeminar({ isOpen: true, isWaiting: waiting });
    };

    const handleSubmitStopSeminar = () => {
        updateSeminar({ is_paused: SEMINAR_STATUS.STOP, seminar_id: seminarIdSelected.current });
    };

    const getContent = (waiting) => (!waiting ? 'このセミナーを停止しますが、よろしいでしょうか？' : (
        <>
            <p>このセミナーを停止しますが、よろしいでしょうか？</p>
            <p>このセミナーは登録され、承認待ち中状態です。</p>
            <p>セミナーを停止する場合、承認できなくなります。</p>
        </>
    ));

    return (
        <>
            <Loader isLoading={isLoading} />
            <GridContainer style={{ marginTop: '20px' }}>
                <GridItem xs={12} sm={6} md={7} className={classes.inputTitle}>
                    <CustomTypography bold>
                        開催日
                    </CustomTypography>
                </GridItem>
                <GridItem xs={12} sm={6} md={5} className={classes.dateRangeSearch}>
                    <SingleDatePickerSearch
                        onChange={(startDate) => handleSetSearchParams('sdate', startDate ? format(startDate, 'yyyy-MM-dd') : '')}
                        defaultValue={null}
                    />
                    <span style={{ marginTop: 'auto', marginBottom: 'auto' }}>〜</span>
                    <SingleDatePickerSearch
                        onChange={(endDate) => handleSetSearchParams('edate', endDate ? format(endDate, 'yyyy-MM-dd') : '')}
                        defaultValue={null}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer style={{ marginTop: '20px' }}>
                <GridItem xs={12} sm={6} md={7} className={classes.inputTitle}>
                    <CustomTypography bold style={{ marginTop: '10px' }}>
                        講演名
                    </CustomTypography>
                </GridItem>
                <GridItem xs={12} sm={6} md={5} style={{ paddingLeft: '8px' }}>
                    <CleanableInput
                        className={classes.input}
                        size="small"
                        defaultValue={params.seminarTitle}
                        placeholder="検索したい講座内容を入力してください"
                        onChange={(title) => handleSetSearchParams('title', title)}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer>
                <GridItem xs={12} md={12} className={classes.buttonSearch}>
                    <Button
                        color="info"
                        round
                        gradient
                        size="md"
                        onClick={handleSearchSeminar}
                        style={{ width: '200px' }}
                    >
                        <b>検索</b>
                    </Button>
                </GridItem>
            </GridContainer>
            {
                createDataView()
            }
            {pagination.page < pagination.total_page && (
                <Box align="center">
                    <Button
                        onClick={handleLoadMoreSeminar}
                        color="info"
                        gradient
                        size="md"
                        round
                    >
                        <LoadMoreIcon />
                        もっと見る
                    </Button>
                </Box>
            )}
            {
                _.isEmpty(seminars) && !isLoading && (
                    <Box
                        xs={12}
                        style={{
                            textAlign: 'center', fontWeight: 'bold', fontSize: '20px', zIndex: 1,
                        }}
                    >
                        <FormattedMessage id="noData" />
                    </Box>
                )
            }
            <DialogEditSeminar
                isOpen={isOpenDialogEdit}
                rooms={roomInfo}
                eventInfo={eventInfo}
                getRoomInfo={handleGetRoomInfo}
                handleClose={() => setOpenDialogEdit(false)}
                detailSeminar={detailSeminar}
                companyId={routeParams.idCompany}
                handleEditSeminar={updateSeminar}
                detailSeminarErrors={detailSeminarErrors}
                clearDetailSeminar={resetDetailSeminar}
                fetchListSeminar={() => {
                    resetSeminarsData();
                    fetchAllSeminars();
                }}

            />
            <Snackbar
                open={openUpdateSuccessAlert}
                onClose={() => setUpdateSuccessAlert(false)}
                message={<FormattedMessage id="apiSuccess.default" />}
                severity="success"
            />
            <Snackbar
                open={openStopSeminarSuccessAlert}
                onClose={() => setOpenStopSeminarSuccessAlert(false)}
                message="このセミナーを停止しました。"
                severity="success"
            />
            <Snackbar
                open={openUpdateErrorAlert.isOpen}
                onClose={() => setUpdateErrorAlert({ isOpen: false, content: '' })}
                message={openUpdateErrorAlert.content}
                severity="error"
            />
            <AlertDialog
                title="確認ダイアログ"
                handleOpen={isOpenConfirmStopSeminar.isOpen}
                handleClose={() => setOpenConfirmStopSeminar({ isOpen: false, isWaiting: false })}
                handleSubmit={handleSubmitStopSeminar}
                contents={getContent(isOpenConfirmStopSeminar.isWaiting)}
            />
        </>
    );
};

ListAllSeminar.defaultProps = {
    isLoading: false,
    updateSuccess: false,
    seminars: {},
    pagination: {},
    eventInfo: {},
    room: {},
    detailSeminar: {},
    updateErrors: {},
    fetchAllSeminars: () => { },
    resetSeminarsData: () => { },
    getInformationRoom: () => { },
    updateSeminar: () => { },
    getDetailSeminar: () => { },
    resetUpdateSeminarData: () => { },
    resetDetailSeminar: () => { },
    detailSeminarErrors: {},
};

ListAllSeminar.propTypes = {
    isLoading: PropTypes.bool,
    updateSuccess: PropTypes.bool,
    fetchAllSeminars: PropTypes.func,
    resetSeminarsData: PropTypes.func,
    getInformationRoom: PropTypes.func,
    getDetailSeminar: PropTypes.func,
    updateSeminar: PropTypes.func,
    resetUpdateSeminarData: PropTypes.func,
    resetDetailSeminar: PropTypes.func,
    pagination: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    room: PropTypes.oneOfType([() => null, PropTypes.object]),
    detailSeminar: PropTypes.oneOfType([() => null, PropTypes.object]),
    updateErrors: PropTypes.oneOfType([() => null, PropTypes.object]),
    seminars: PropTypes.oneOfType([() => null, PropTypes.object]),
    detailSeminarErrors: PropTypes.oneOfType([() => null, PropTypes.object]),
};

ActionRow.defaultProps = {
    onPause: () => { },
    onEdit: () => { },
    stopped: false,
};

ActionRow.propTypes = {
    onPause: PropTypes.func,
    onEdit: PropTypes.func,
    stopped: PropTypes.bool,
};

export default ListAllSeminar;
